/**
 * Submitter Identity + Form Tokens — pure, dependency-free helpers.
 *
 * A submission's OWNER identity (firstName / lastName / dob / email / phone) is
 * configured per form or package (`identity_fields_json`) and, once known, used
 * to prefill legacy Form.io `{{token}}` placeholders. All logic here is pure so
 * it can run identically server-side (assigned / package flows) and client-side
 * (public single form). Nothing here touches the DB, crypto, or PHI logging.
 *
 * PHI RULE: validator error strings reference field KEYS ONLY and MUST NEVER
 * echo a submitted value — they end up in 400 response bodies. Callers must
 * never `context.log` them either.
 */
import type { IdentityFieldConfig, IdentityFieldKey, SubmitterIdentity } from '../types/index.js';
import { DEFAULT_IDENTITY_FIELDS, IDENTITY_TOKEN_MAP } from '../constants/index.js';

const CATALOG: readonly IdentityFieldKey[] = ['firstName', 'lastName', 'dob', 'email', 'phone'];
const MAX_FIELD_LEN = 200;
const DOB_RE = /^\d{4}-\d{2}-\d{2}$/;
/** A whole-value `{{token}}`-shaped defaultValue (letters, spaces, underscores). */
const TOKEN_SHAPE_RE = /^\{\{[a-zA-Z_ ]+\}\}$/;

function hasOwn(obj: object, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

function isCatalogKey(k: unknown): k is IdentityFieldKey {
  return typeof k === 'string' && (CATALOG as readonly string[]).includes(k);
}

/**
 * Parse an `identity_fields_json` column value into an ordered config array.
 * NULL/undefined → DEFAULT_IDENTITY_FIELDS. '[]' → [] (explicitly no identity
 * step). Invalid JSON / non-array / unknown keys / wrong shapes → filtered (bad
 * entries dropped) or, when nothing parses, the default. Never throws.
 */
export function parseIdentityConfig(raw: string | null | undefined): IdentityFieldConfig[] {
  if (raw === null || raw === undefined) return [...DEFAULT_IDENTITY_FIELDS];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [...DEFAULT_IDENTITY_FIELDS];
  }
  if (!Array.isArray(parsed)) return [...DEFAULT_IDENTITY_FIELDS];
  // An explicitly empty array means "no identity step" — honour it verbatim.
  if (parsed.length === 0) return [];
  const seen = new Set<IdentityFieldKey>();
  const out: IdentityFieldConfig[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as Record<string, unknown>;
    if (!isCatalogKey(e['key']) || seen.has(e['key'])) continue;
    seen.add(e['key']);
    out.push({ key: e['key'], required: e['required'] === true });
  }
  // Every entry was garbage → fall back to the default rather than serve an
  // effectively-configless empty step.
  return out.length > 0 ? out : [...DEFAULT_IDENTITY_FIELDS];
}

/** Serialize a config array for the `identity_fields_json` column. */
export function serializeIdentityConfig(cfg: IdentityFieldConfig[]): string {
  return JSON.stringify(cfg.map((c) => ({ key: c.key, required: c.required === true })));
}

export type ValidateSubmitterResult =
  | { ok: true; value: Omit<SubmitterIdentity, 'source'> }
  | { ok: false; error: string };

/** Today's UTC date + 1 day, as an ISO YYYY-MM-DD string. */
function dobUpperBoundIso(): string {
  const now = new Date();
  const todayUtcMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(todayUtcMs + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** A DOB must be a real calendar date and sit in [1900-01-01, today-UTC + 1 day]. */
function isValidDob(dob: string): boolean {
  if (!DOB_RE.test(dob)) return false;
  // Regex guarantees exactly three numeric parts.
  const y = Number(dob.slice(0, 4));
  const m = Number(dob.slice(5, 7));
  const d = Number(dob.slice(8, 10));
  // Round-trip through UTC components: 2023-02-30 rolls to 03-02, so the parts
  // no longer match — rejected.
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return false;
  // ISO string comparison is timezone-proof: a UTC+13 patient entering their
  // local "today" still passes against the +1-day upper bound.
  return dob >= '1900-01-01' && dob <= dobUpperBoundIso();
}

/**
 * Validate patient-entered identity input against a config. Trims strings, caps
 * each field at 200 chars, enforces required-per-config, validates DOB, and
 * strips any key not in the config. Error strings name field KEYS only — never
 * the submitted value.
 */
export function validateSubmitterInput(input: unknown, cfg: IdentityFieldConfig[]): ValidateSubmitterResult {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, error: 'identity input must be an object' };
  }
  const src = input as Record<string, unknown>;
  const value: Omit<SubmitterIdentity, 'source'> = {};
  for (const field of cfg) {
    const rawVal = src[field.key];
    let str: string | undefined;
    if (typeof rawVal === 'string') {
      const trimmed = rawVal.trim().slice(0, MAX_FIELD_LEN);
      if (trimmed.length > 0) str = trimmed;
    }
    if (str === undefined) {
      if (field.required) return { ok: false, error: `${field.key} is required` };
      continue;
    }
    if (field.key === 'dob' && !isValidDob(str)) {
      return { ok: false, error: 'dob must be a valid date (YYYY-MM-DD)' };
    }
    value[field.key] = str;
  }
  return { ok: true, value };
}

type SchemaNode = Record<string, unknown>;

/** Deep-clone (schemas are plain JSON) then visit every node once. */
function cloneAndWalk<T>(schema: T, visit: (node: SchemaNode) => void): T {
  // Guard non-object input (undefined/null/primitive). A not-yet-loaded schema reaches here
  // (e.g. collectPrefillData in a useMemo that runs before the fetch resolves), and
  // JSON.parse(JSON.stringify(undefined)) throws `"undefined" is not valid JSON`.
  if (schema === null || typeof schema !== 'object') return schema;
  const clone = JSON.parse(JSON.stringify(schema)) as T;
  const walk = (node: unknown): void => {
    if (Array.isArray(node)) {
      for (const n of node) walk(n);
      return;
    }
    if (!node || typeof node !== 'object') return;
    const c = node as SchemaNode;
    visit(c);
    if (Array.isArray(c['components'])) walk(c['components']);
    if (Array.isArray(c['columns'])) walk(c['columns']);
    if (Array.isArray(c['rows'])) {
      for (const r of c['rows'] as unknown[]) walk(r);
    }
  };
  walk(clone as unknown);
  return clone;
}

/**
 * Format an identity value for the specific component it prefills. A `dob` (ISO
 * `YYYY-MM-DD`) landing on a Form.io `datetime` component is widened to a
 * local-midnight ISO datetime — the widget renders a bare date string as blank,
 * but ingests `2000-01-01T00:00:00` and displays it in the component's `format`.
 * NO trailing 'Z': a UTC datetime would roll back a day in negative-offset
 * browsers (US) and show the DOB one day early. Every other field/type passes
 * through unchanged (textfields render the bare value fine).
 */
function formatIdentityValueForComponent(
  fieldKey: IdentityFieldKey,
  value: string,
  component: SchemaNode,
): string {
  if (fieldKey === 'dob' && component['type'] === 'datetime' && DOB_RE.test(value)) {
    return `${value}T00:00:00`;
  }
  return value;
}

/**
 * Return a NEW schema with each component whose `defaultValue` EXACTLY equals a
 * known identity token replaced by the identity value (missing → ''). Substring
 * matches are never touched. Input is never mutated. A `dob` on a `datetime`
 * component is widened to an ISO datetime so the widget actually renders it
 * (see {@link formatIdentityValueForComponent}).
 */
export function substituteIdentityTokens<T>(schema: T, identity: Partial<SubmitterIdentity>): T {
  return cloneAndWalk(schema, (c) => {
    const dv = c['defaultValue'];
    if (typeof dv === 'string' && hasOwn(IDENTITY_TOKEN_MAP, dv)) {
      const fieldKey = IDENTITY_TOKEN_MAP[dv] as IdentityFieldKey;
      const val = identity[fieldKey];
      c['defaultValue'] = typeof val === 'string' ? formatIdentityValueForComponent(fieldKey, val, c) : '';
    }
  });
}

/**
 * Return a NEW schema with any `{{...}}`-shaped whole-value `defaultValue`
 * blanked to '' — EXCEPT known identity tokens, which SURVIVE (the anonymous
 * public GET is sweep-only; the client substitutes them later). This fixes the
 * live bug where imported forms (e.g. {{orgId}}) render raw braces.
 */
export function sweepUnknownTokens<T>(schema: T): T {
  return cloneAndWalk(schema, (c) => {
    const dv = c['defaultValue'];
    if (typeof dv === 'string' && TOKEN_SHAPE_RE.test(dv) && !hasOwn(IDENTITY_TOKEN_MAP, dv)) {
      c['defaultValue'] = '';
    }
  });
}

/**
 * Serve-time schema prep for identity-authoritative server paths: substitute
 * known tokens with the identity (an EMPTY identity blanks them to ''), then
 * sweep unknown tokens. The result never contains raw braces.
 */
export function prepareSchemaForServe<T>(schema: T, identity?: Partial<SubmitterIdentity>): T {
  return sweepUnknownTokens(substituteIdentityTokens(schema, identity ?? {}));
}

/** Form.io component types whose widget does NOT paint a schema-level `defaultValue`
 *  on initial load — the value must arrive through SUBMISSION DATA instead. Textfields
 *  paint their defaultValue directly, so they're not here. */
const DATE_COMPONENT_TYPES = new Set(['datetime', 'day', 'date']);

/**
 * Build a Form.io submission-data map ({ [componentKey]: value }) that forces
 * date-family components (datetime / day / date) to actually render their resolved
 * `defaultValue`. Reason: Form.io renders a textfield's schema `defaultValue` into the
 * input, but a datetime/calendar widget does NOT paint a schema `defaultValue` on load —
 * the value only shows when it flows through submission data (the same path that reloads a
 * saved submission). So the DOB prefill was silently dropped while First/Last (textfields)
 * worked. Call this on the ALREADY-substituted schema (client- or server-side) and pass the
 * result as `submission={{ data }}` alongside `form={schema}`. Unresolved `{{token}}` and
 * empty defaults are skipped. Pure; input is not mutated.
 */
export function collectPrefillData(schema: unknown): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  cloneAndWalk(schema, (c) => {
    const type = c['type'];
    const key = c['key'];
    const dv = c['defaultValue'];
    if (
      typeof type === 'string' && DATE_COMPONENT_TYPES.has(type) &&
      typeof key === 'string' && key.length > 0 &&
      typeof dv === 'string' && dv.length > 0 && !TOKEN_SHAPE_RE.test(dv)
    ) {
      data[key] = dv;
    }
  });
  return data;
}
